'use client'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons'
import * as SelectPrimitive from '@radix-ui/react-select'
import * as React from 'react'

import { cn } from '~/lib/utils'

// Context to share open state and disabled state between SpecialSelect and SpecialSelectTrigger
const SpecialSelectContext = React.createContext<{
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  isDisabled: boolean
}>({
  isOpen: false,
  setIsOpen() {},
  isDisabled: false,
})

const SpecialSelect = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
>(({ children, disabled, ...props }, _ref) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <SpecialSelectContext.Provider
      value={{ isOpen, setIsOpen, isDisabled: !!disabled }}
    >
      <SelectPrimitive.Root
        {...props}
        disabled={disabled}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        {children}
      </SelectPrimitive.Root>
    </SpecialSelectContext.Provider>
  )
})
SpecialSelect.displayName = 'SpecialSelect'

const SpecialSelectGroup = SelectPrimitive.Group

const SpecialSelectValue = SelectPrimitive.Value

interface SpecialSelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> {
  label: string
  hasValue?: boolean
  isNotchedClassName?: string
  notIsNotchedClassName?: string
}

const SpecialSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SpecialSelectTriggerProps
>(
  (
    {
      className,
      children,
      label,
      hasValue,
      isNotchedClassName,
      notIsNotchedClassName,
      ...props
    },
    ref,
  ) => {
    const { isOpen, isDisabled } = React.useContext(SpecialSelectContext)
    const labelRef = React.useRef<HTMLSpanElement>(null)
    const [notchWidth, setNotchWidth] = React.useState(0)

    // Border color based on disabled state
    const borderColor = isDisabled ? 'border-black/30' : 'border-black'

    // Calculate notch width when label floats
    React.useEffect(() => {
      if (labelRef.current && (hasValue || isOpen)) {
        // Get label width and add padding (8px on each side = 16px total)
        const width = labelRef.current.offsetWidth * 0.75 + 8
        setNotchWidth(width)
      }
      else {
        setNotchWidth(0)
      }
    }, [hasValue, isOpen, label])

    const isNotched = hasValue || isOpen

    return (
      <div className="relative w-full">
        {/* Notched Outline */}
        <div
          className={cn(
            'absolute inset-0 pointer-events-none flex',
            'text-gray-400',
          )}
        >
          {/* Leading */}
          <div
            className={cn(
              'border-t border-b border-l rounded-l-[10px]',
              borderColor,
              'w-3',
            )}
          />

          {/* Notch */}
          <div
            className={cn(
              'border-b transition-all duration-200 relative',
              borderColor,
              !isNotched && 'border-t',
            )}
            style={{
              width: isNotched ? `${notchWidth}px` : '0px',
              borderTopWidth: isNotched ? '0' : undefined,
            }}
          >
            {/* Floating Label */}
            <span
              ref={labelRef}
              className={cn(
                'absolute px-1 bg-transparent pointer-events-none whitespace-nowrap origin-top-left',
                'transition-all duration-200',
                'font-cera font-medium tracking-[-0.39px]',
                isDisabled ? 'text-black/30' : 'text-black/60',
                isNotched
                  ? 'text-lg left-[5px] top-[-10px] scale-75'
                  : 'text-lg left-0 top-[4px] scale-100',
                isNotched ? isNotchedClassName : notIsNotchedClassName,
              )}
            >
              {label}
            </span>
          </div>

          {/* Trailing */}
          <div
            className={cn(
              'border-t border-b border-r rounded-r-[10px] flex-1',
              borderColor,
            )}
          />
        </div>

        {/* Select Trigger */}
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(
            'relative w-full bg-transparent outline-none px-4 h-[38px] flex items-center justify-between',
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
            isDisabled ? 'text-black/30' : 'text-black',
            'font-cera text-lg font-medium tracking-[-0.39px]',
            className,
          )}
          {...props}
        >
          <span
            className={cn(
              'flex-1 text-left truncate w-[10px]',
              !hasValue && 'opacity-0',
            )}
          >
            {children}
          </span>
          <SelectPrimitive.Icon asChild>
            <ChevronDownIcon
              className={cn(
                'size-5 ml-2',
                isDisabled ? 'text-black/30' : 'text-black',
              )}
            />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
      </div>
    )
  },
)
SpecialSelectTrigger.displayName = 'SpecialSelectTrigger'

const SpecialSelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
))
SpecialSelectScrollUpButton.displayName
  = SelectPrimitive.ScrollUpButton.displayName

const SpecialSelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
))
SpecialSelectScrollDownButton.displayName
  = SelectPrimitive.ScrollDownButton.displayName

const SpecialSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-[#f9f6f2] text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper'
        && 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SpecialSelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper'
          && 'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SpecialSelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SpecialSelectContent.displayName = SelectPrimitive.Content.displayName

const SpecialSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SpecialSelectItem.displayName = SelectPrimitive.Item.displayName

export {
  SpecialSelect,
  SpecialSelectContent,
  SpecialSelectGroup,
  SpecialSelectItem,
  SpecialSelectScrollDownButton,
  SpecialSelectScrollUpButton,
  SpecialSelectTrigger,
  SpecialSelectValue,
}
